import Repository from "@/repository";

const storageKey = "clientToken";
const userKey = "clientUser";

function parseJson(source) {
  try {
    return source ? JSON.parse(source) : null;
  } catch (error) {
    return null;
  }
}

function loadSession() {
  const session = parseJson(localStorage.getItem(storageKey));
  const user = parseJson(localStorage.getItem(userKey));
  return {
    access: session?.access || null,
    refresh: session?.refresh || null,
    user: user || null,
  };
}

function saveSession({ access, refresh }) {
  localStorage.setItem(storageKey, JSON.stringify({ access, refresh }));
}

function saveUser(user) {
  localStorage.setItem(userKey, JSON.stringify(user));
}

function clearPersistedSession() {
  localStorage.removeItem(storageKey);
  localStorage.removeItem(userKey);
}

function applyAccessToken(access) {
  Repository.connector.setAuthHeaders(access);
}

function messageFromResetPasswordRequestError(error) {
  if (error?.status !== 400 || error?.email == null) return null;
  const rawList = Array.isArray(error.email) ? error.email : [error.email];
  const first = String(rawList[0] || "").trim();
  if (!first) return null;
  const lower = first.toLowerCase();
  if (
    lower.includes("enter a valid email") ||
    lower.includes("valid email address")
  ) {
    return "Укажите корректный адрес электронной почты.";
  }
  return "Проверьте правильность адреса электронной почты.";
}

const REGISTER_API_META_KEYS = new Set([
  "status",
  "method",
  "url",
  "message",
  "detail",
]);

function firstApiErrorString(value) {
  if (Array.isArray(value)) return String(value[0] || "").trim();
  if (typeof value === "string") return value.trim();
  return "";
}

function translateRegisterApiMessage(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (
    lower.includes("enter a valid email") ||
    lower.includes("valid email address")
  ) {
    return "Укажите корректный адрес электронной почты.";
  }
  if (lower.includes("this field is required")) {
    return "Заполните поле.";
  }
  if (lower.includes("may not be blank")) {
    return "Поле не может быть пустым.";
  }
  if (
    lower.includes("already exists") ||
    lower.includes("already been taken") ||
    lower.includes("must make a unique set") ||
    lower.includes("unique")
  ) {
    if (lower.includes("email")) {
      return "Этот адрес почты уже зарегистрирован.";
    }
    if (lower.includes("username") || lower.includes("user")) {
      return "Это имя пользователя уже занято.";
    }
    return "Такие данные уже используются.";
  }
  const atLeast = lower.match(/at least (\d+) character/);
  if (atLeast) {
    return `Минимум ${atLeast[1]} символов.`;
  }
  if (
    lower.includes("too short") ||
    lower.includes("too common") ||
    lower.includes("entirely numeric") ||
    lower.includes("password is too")
  ) {
    return "Пароль слишком простой или короткий. Выберите более надёжный пароль.";
  }
  if (lower.includes("password")) {
    return "Проверьте требования к паролю.";
  }
  return "Проверьте введённые данные.";
}

function mapRegisterFormFieldKey(key) {
  if (key === "password1" || key === "password2" || key === "re_password") {
    return "password";
  }
  return key;
}

function parseRegister400Error(error) {
  if (error?.status !== 400) return null;
  const fieldErrors = {};
  let generalMessage = "";
  for (const key of Object.keys(error)) {
    if (REGISTER_API_META_KEYS.has(key)) continue;
    const raw = firstApiErrorString(error[key]);
    if (!raw) continue;
    const translated = translateRegisterApiMessage(raw);
    if (key === "non_field_errors") {
      generalMessage = translated;
    } else {
      fieldErrors[mapRegisterFormFieldKey(key)] = translated;
    }
  }
  if (!generalMessage && error.detail && typeof error.detail === "string") {
    generalMessage = translateRegisterApiMessage(error.detail);
  }
  if (
    !Object.keys(fieldErrors).length &&
    !generalMessage &&
    typeof error.message === "string" &&
    error.message &&
    error.message !== "Request failed"
  ) {
    generalMessage = translateRegisterApiMessage(error.message);
  }
  if (!Object.keys(fieldErrors).length && !generalMessage) return null;
  return { fieldErrors, generalMessage: generalMessage || null };
}

async function persistSessionFromTokens(commit, access, refresh) {
  const accessToken = String(access || "").trim();
  if (!accessToken) {
    throw new Error("Нет токена доступа");
  }
  const refreshToken = refresh ? String(refresh).trim() : null;
  applyAccessToken(accessToken);
  saveSession({ access: accessToken, refresh: refreshToken });
  const user = await Repository.Profile.me();
  saveUser(user);
  commit("setSession", {
    access: accessToken,
    refresh: refreshToken,
    user,
  });
}

const initialState = loadSession();
if (initialState.access) {
  applyAccessToken(initialState.access);
}

export default {
  namespaced: true,
  state: () => ({
    access: initialState.access,
    refresh: initialState.refresh,
    user: initialState.user,
    loading: false,
    error: null,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.access),
    userName: (state) => state.user?.username || "",
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setError(state, value) {
      state.error = value;
    },
    setSession(state, payload) {
      state.access = payload.access || null;
      state.refresh = payload.refresh || null;
      state.user = payload.user || null;
    },
    clearSession(state) {
      state.access = null;
      state.refresh = null;
      state.user = null;
      state.error = null;
    },
  },
  actions: {
    async refreshSession({ state, commit }) {
      if (!state.refresh) {
        return false;
      }
      try {
        const { access, refresh } = await Repository.Auth.refresh({
          refresh: state.refresh,
        });
        applyAccessToken(access);
        const nextRefresh = refresh || state.refresh;
        saveSession({ access, refresh: nextRefresh });
        commit("setSession", { access, refresh: nextRefresh, user: state.user });
        return true;
      } catch (error) {
        Repository.connector.clearAuthHeaders();
        clearPersistedSession();
        commit("clearSession");
        return false;
      }
    },
    async login({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const { access, refresh } = await Repository.Auth.login(payload);
        applyAccessToken(access);
        saveSession({ access, refresh });
        const user = await Repository.Profile.me();
        saveUser(user);
        commit("setSession", { access, refresh, user });
        return { ok: true };
      } catch (error) {
        const message = error?.message || "Не удалось выполнить вход";
        commit("setError", message);
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    async loginWithOAuthTokens({ commit }, { access, refresh, user }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const accessToken = String(access || "").trim();
        if (!accessToken) {
          throw new Error("Нет токена доступа");
        }
        const refreshToken = refresh ? String(refresh).trim() : null;
        if (user && typeof user === "object") {
          applyAccessToken(accessToken);
          saveSession({ access: accessToken, refresh: refreshToken });
          saveUser(user);
          commit("setSession", {
            access: accessToken,
            refresh: refreshToken,
            user,
          });
          return { ok: true };
        }
        await persistSessionFromTokens(commit, access, refresh);
        return { ok: true };
      } catch (error) {
        const message = error?.message || "Не удалось выполнить вход";
        commit("setError", message);
        Repository.connector.clearAuthHeaders();
        clearPersistedSession();
        commit("clearSession");
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    async register({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        await Repository.Auth.register(payload);
        return { ok: true };
      } catch (error) {
        const parsed = parseRegister400Error(error);
        if (parsed) {
          commit("setError", parsed.generalMessage || null);
          return {
            ok: false,
            fieldErrors: parsed.fieldErrors,
            message: parsed.generalMessage || null,
          };
        }
        const message =
          translateRegisterApiMessage(
            error?.detail || error?.message || ""
          ) || "Не удалось зарегистрироваться";
        commit("setError", message);
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    logout({ commit }) {
      Repository.connector.clearAuthHeaders();
      clearPersistedSession();
      commit("clearSession");
    },
    updateUser({ state, commit }, partialUser) {
      if (!state.user || !partialUser) return;
      const user = { ...state.user, ...partialUser };
      saveUser(user);
      commit("setSession", { access: state.access, refresh: state.refresh, user });
    },
    async requestResetCode({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        await Repository.Auth.restorePassword(payload);
        return { ok: true };
      } catch (error) {
        const message =
          messageFromResetPasswordRequestError(error) ||
          error?.detail ||
          error?.message ||
          "Не удалось отправить код";
        commit("setError", message);
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    async verifyResetCode({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const data = await Repository.Auth.verifyResetPasswordCode(payload);
        const token = data?.token ?? data?.access ?? null;
        return { ok: true, token };
      } catch (error) {
        const message =
          error?.status === 401
            ? "Неверный код. Проверьте код из письма."
            : error?.detail || error?.message || "Не удалось проверить код";
        commit("setError", message);
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    async setNewPassword({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const data = await Repository.Auth.setNewPassword(payload);
        const access = data?.access;
        const refresh = data?.refresh;
        if (access) {
          await persistSessionFromTokens(commit, access, refresh);
          return { ok: true, authenticated: true };
        }
        return { ok: true };
      } catch (error) {
        const message = error?.message || "Не удалось сохранить пароль";
        commit("setError", message);
        Repository.connector.clearAuthHeaders();
        clearPersistedSession();
        commit("clearSession");
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
    async verifyEmail({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const { access, refresh } = await Repository.Auth.verify(payload);
        applyAccessToken(access);
        saveSession({ access, refresh });
        const user = await Repository.Profile.me();
        saveUser(user);
        commit("setSession", { access, refresh, user });
        return { ok: true };
      } catch (error) {
        const message = error?.message || "Неверный код";
        commit("setError", message);
        return { ok: false, message };
      } finally {
        commit("setLoading", false);
      }
    },
  },
};

